import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ArchiveIcon, MailIcon, PlusIcon } from 'lucide-react';
import { useAppState } from '../../contexts/AppStateContext';
import { inviteLabels } from '../../data/labels';
import { memberPalette } from '../../data/neutralTemplate';
import { Member } from '../../types/family';
import { uid } from '../../utils/db';
import { Avatar } from '../Avatar';
import { Button } from '../ui/Button';
import { Chip, Field } from '../ui/Form';
import { Sheet } from '../ui/Sheet';
export function MembersSettings() {
  const {
    family,
    renameFamily,
    saveMember,
    archiveMember,
    setInvite
  } = useAppState();
  const [name, setName] = useState(family.name);
  const [editing, setEditing] = useState<Member | null>(null);
  useEffect(() => setName(family.name), [family.name]);
  return <div className="space-y-6" data-id="element-591" data-mp="31qmqsdtvload">
      <form className="flex flex-col gap-2 sm:flex-row sm:items-end" onSubmit={e => {
      e.preventDefault();
      if (name.trim() && name.trim() !== family.name) {
        renameFamily(name.trim());
        toast.success('Ime družine shranjeno.');
      }
    }} data-id="element-592" data-mp="2ws1zj3wlqey2">
        <div className="flex-1" data-id="element-593" data-mp="yzpgb2gl1w66">
          <Field label="Ime družine" hint="Ločeno od imena aplikacije »Do it, don't poo it«." data-id="element-594" data-mp="z0r0bwfktt01">
            {id => <input id={id} className="field" value={name} onChange={e => setName(e.target.value)} data-id="element-595" data-mp="13z2bqblisbkd" />}
          </Field>
        </div>
        <Button type="submit" variant="secondary" className="sm:mb-5" disabled={!name.trim() || name.trim() === family.name} data-id="element-596" data-mp="1rqh7flisjumn">
          Shrani ime
        </Button>
      </form>

      <div data-id="element-597" data-mp="1hy1oolzo720p">
        <div className="mb-2 flex items-center justify-between" data-id="element-598" data-mp="1hw7d3yaagm9a">
          <h3 className="font-display text-lg font-semibold" data-id="element-599" data-mp="1mw3ia0uq2jqf">Člani · {family.members.filter(m => !m.archived).length}</h3>
          <Button size="sm" onClick={() => setEditing({
          id: uid('m'),
          name: '',
          role: 'otrok',
          color: memberPalette[family.members.length % memberPalette.length],
          invite: 'brez'
        })} data-id="element-600" data-mp="2alnzofb898xl">
            <PlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-601" data-mp="2p95ywy60tbcm" /> Dodaj člana
          </Button>
        </div>
        <ul className="divide-y divide-line rounded-3xl border-[1.5px] border-line bg-surface" data-id="element-602" data-mp="2kbctw8ggendx">
          {family.members.map(m => <li key={m.id} className={`flex flex-wrap items-center gap-3 px-4 py-3 ${m.archived ? 'opacity-60' : ''}`} data-id="element-603" data-mp="2acf657quqxvz">
              <Avatar member={m} data-id="element-604" data-mp="26b22937bl4rp" />
              <button type="button" onClick={() => setEditing(m)} className="min-w-0 flex-1 text-left" data-id="element-605" data-mp="26cd42xh7g2g6">
                <span className="block font-bold hover:underline" data-id="element-606" data-mp="21isf1uxlait4">{m.name}</span>
                <span className="block text-xs text-muted" data-id="element-607" data-mp="1wlsbprs4ju5p">
                  {m.role === 'skrbnik' ? 'Skrbnik' : 'Otrok'} · {m.archived ? 'arhiviran (zgodovina ostane)' : inviteLabels[m.invite]}
                </span>
              </button>
              {!m.archived && m.invite !== 'sprejeto' && <span className="flex gap-1" data-id="element-608" data-mp="1imiwovfnn63b">
                  <Button size="sm" variant="secondary" onClick={() => {
              setInvite(m.id, 'poslano');
              toast.success(`Vzorčno vabilo za ${m.name} pripravljeno. Pravo pošiljanje še ni izvedeno.`);
            }} data-id="element-609" data-mp="3u8kzlpg5oq72">
                    <MailIcon className="h-4 w-4" aria-hidden="true" data-id="element-610" data-mp="c1wsxv5gjuza" /> {m.invite === 'poslano' ? 'Ponovno' : 'Povabi'}
                  </Button>
                  {m.invite === 'poslano' && <Button size="sm" variant="ghost" onClick={() => setInvite(m.id, 'preklicano')} data-id="element-611" data-mp="3fkq5c4mi2drv">
                      Prekliči
                    </Button>}
                </span>}
              <button type="button" onClick={() => archiveMember(m.id)} aria-label={m.archived ? `Obnovi ${m.name}` : `Arhiviraj ${m.name}`} className="grid h-9 w-9 place-items-center rounded-xl text-muted hover:bg-canvas" data-id="element-612" data-mp="3pb2phvqek4xh">
                <ArchiveIcon className="h-4 w-4" data-id="element-613" data-mp="2wr4811kaikfb" />
              </button>
            </li>)}
        </ul>
        <p className="mt-2 text-xs text-muted" data-id="element-614" data-mp="ybdfppp46mi6">Vabila so v prototipu vzorčna (povezava ali koda). Način prijave je še odprta odločitev.</p>
      </div>
      <MemberSheet member={editing} onClose={() => setEditing(null)} onSave={saveMember} data-id="element-615" data-mp="1c3jw9cw6ivi5" />
    </div>;
}
function MemberSheet({
  member,
  onClose,
  onSave
}: {
  member: Member | null;
  onClose: () => void;
  onSave: (m: Member) => void;
}) {
  const [m, setM] = useState<Member | null>(member);
  useEffect(() => setM(member), [member]);
  if (!m) return <Sheet open={false} onClose={onClose} title="" children={null} data-id="element-616" data-mp="1v1nrr4j58w1h" />;
  const set = (p: Partial<Member>) => setM(cur => cur ? {
    ...cur,
    ...p
  } : cur);
  return <Sheet open onClose={onClose} title={m.name || 'Nov član'} footer={<Button className="w-full" disabled={!m.name.trim()} onClick={() => {
    onSave({
      ...m,
      name: m.name.trim(),
      invite: m.contact && m.invite === 'brez' ? 'poslano' : m.invite
    });
    onClose();
  }} data-id="element-618" data-mp="o4wk1cl5pjc">
          Shrani člana
        </Button>} data-id="element-617" data-mp="1q32pz0o8u4ax">
      <div className="space-y-4" data-id="element-619" data-mp="2cct0ec5h2qwc">
        <Field label="Ime" data-id="element-620" data-mp="1yk3ee7py1lz8">{id => <input id={id} className="field" value={m.name} onChange={e => set({
          name: e.target.value
        })} data-id="element-621" data-mp="1yisbsb88ik9p" />}</Field>
        <div data-id="element-622" data-mp="22jc6l85c5bso">
          <p className="mb-1.5 text-sm font-bold" data-id="element-623" data-mp="22j2e9dwtisjt">Vloga</p>
          <div className="flex gap-2" data-id="element-624" data-mp="1okcfltucoktc">
            <Chip active={m.role === 'otrok'} onClick={() => set({
            role: 'otrok'
          })} data-id="element-625" data-mp="1ongv3nd9n2zb">
              Otrok
            </Chip>
            <Chip active={m.role === 'skrbnik'} onClick={() => set({
            role: 'skrbnik'
          })} data-id="element-626" data-mp="1fkz5cyyejs7k">
              Skrbnik
            </Chip>
          </div>
          <p className="mt-1 text-xs text-muted" data-id="element-627" data-mp="1jmt4v3wv2q65">Pravica potrjevanja izhaja iz vloge, ne iz imena.</p>
        </div>
        <div className="grid grid-cols-2 gap-3" data-id="element-628" data-mp="39zi72zb4droj">
          <Field label="Rojstni podatki" data-id="element-629" data-mp="3e0u1di4xv132">{id => <input id={id} className="field" value={m.birth ?? ''} onChange={e => set({
            birth: e.target.value || undefined
          })} placeholder="npr. 2011-09" data-id="element-630" data-mp="3dzsg6ku7ky7m" />}</Field>
          <Field label="E-pošta ali telefon" data-id="element-631" data-mp="lsidbwastqjo">{id => <input id={id} className="field" value={m.contact ?? ''} onChange={e => set({
            contact: e.target.value || undefined
          })} data-id="element-632" data-mp="lrgqkukf7fm4" />}</Field>
        </div>
        <div data-id="element-633" data-mp="csvbxai2kkqm">
          <p className="mb-1.5 text-sm font-bold" data-id="element-634" data-mp="ct50ctnguju7">Barva</p>
          <div className="flex flex-wrap gap-2" data-id="element-635" data-mp="19icmo3vwswz6">
            {memberPalette.map(c => <button key={c} type="button" aria-label={`Barva ${c}`} aria-pressed={m.color === c} onClick={() => set({
            color: c
          })} className={`h-9 w-9 rounded-full border-4 ${m.color === c ? 'border-ink' : 'border-surface'}`} style={{
            backgroundColor: c
          }} data-id="element-636" data-mp="15gr4q44ipsi4" />)}
          </div>
        </div>
      </div>
    </Sheet>;
}